import { useState, useEffect } from "react";
import {
  collection,
  query,
  getDocs,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "./useTranslation";
import { translatePreservingFrench } from "../utils/lang";

export const useSeriesEpisodes = (seriesId) => {
  // Original Firestore data (always in English)
  const [originalEpisodes, setOriginalEpisodes] = useState([]);
  const [originalSeriesData, setOriginalSeriesData] = useState(null);

  // Translated data for display
  const [episodes, setEpisodes] = useState([]);
  const [seriesData, setSeriesData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);

  const { currentLanguage, translateText } = useTranslation();

  // Translation cache to avoid re-translating same content
  const [translationCache, setTranslationCache] = useState(new Map());

  const TRANSLATION_CACHE_VERSION = "10";

  // Helper function to get cached translation or translate and cache
  const getCachedTranslation = async (text, targetLang) => {
    if (!text || targetLang === "en") return text;

    const normalizedTarget = (targetLang || "").toLowerCase();
    const cacheKey = `${TRANSLATION_CACHE_VERSION}|${normalizedTarget}|${text}`;
    if (translationCache.has(cacheKey)) return translationCache.get(cacheKey);

    try {
      const out =
        normalizedTarget === "fr"
          ? await translatePreservingFrench(text, "fr", translateText)
          : await translateText(text, normalizedTarget);

      setTranslationCache((prev) => new Map(prev.set(cacheKey, out)));
      return out;
    } catch (e) {
      console.error("Translation error:", e);
      return text;
    }
  };

  // Translate series data
  const translateSeriesData = async (seriesToTranslate) => {
    if (currentLanguage === "en" || !seriesToTranslate) {
      return seriesToTranslate;
    }

    return {
      ...seriesToTranslate,
      title: await getCachedTranslation(
        seriesToTranslate.title,
        currentLanguage
      ),
      description: seriesToTranslate.description
        ? await getCachedTranslation(
            seriesToTranslate.description,
            currentLanguage
          )
        : seriesToTranslate.description,
      shortDescription: seriesToTranslate.shortDescription
        ? await getCachedTranslation(
            seriesToTranslate.shortDescription,
            currentLanguage
          )
        : seriesToTranslate.shortDescription,
      longDescription: seriesToTranslate.longDescription
        ? await getCachedTranslation(
            seriesToTranslate.longDescription,
            currentLanguage
          )
        : seriesToTranslate.longDescription,
    };
  };

  // Translate episodes
  const translateEpisodes = async (episodesToTranslate) => {
    if (currentLanguage === "en") {
      return episodesToTranslate;
    }

    return Promise.all(
      episodesToTranslate.map(async (episode) => ({
        ...episode,
        title: await getCachedTranslation(episode.title, currentLanguage),
        description: episode.description
          ? await getCachedTranslation(episode.description, currentLanguage)
          : episode.description,
        shortDescription: episode.shortDescription
          ? await getCachedTranslation(
              episode.shortDescription,
              currentLanguage
            )
          : episode.shortDescription,
        longDescription: episode.longDescription
          ? await getCachedTranslation(episode.longDescription, currentLanguage)
          : episode.longDescription,
      }))
    );
  };

  // Fetch data from Firestore (runs when seriesId changes)
  useEffect(() => {
    if (!seriesId) {
      setOriginalEpisodes([]);
      setOriginalSeriesData(null);
      setEpisodes([]);
      setSeriesData(null);
      setLoading(false);
      return;
    }

    let unsubscribe;

    const fetchSeriesAndEpisodes = async () => {
      try {
        setLoading(true);
        setError(null);

        // First get the series document using the id field
        const seriesRef = collection(db, "content", "main", "series");
        const seriesQuery = query(seriesRef, where("id", "==", seriesId));
        const seriesSnapshot = await getDocs(seriesQuery);

        if (seriesSnapshot.empty) {
          console.error("No series found with id:", seriesId);
          setError("Series not found");
          setLoading(false);
          return;
        }

        // Get the series data and Firestore document ID
        const firestoreSeriesId = seriesSnapshot.docs[0].id;
        const seriesDocData = seriesSnapshot.docs[0].data();
        const series = {
          ...seriesDocData,
          firestoreId: firestoreSeriesId,
        };
        setOriginalSeriesData(series);

        // Now get episodes using the Firestore document ID
        const episodesRef = collection(
          db,
          "content",
          "main",
          "series",
          firestoreSeriesId,
          "episodes",
        );

        const episodesQuery = query(
          episodesRef,
          orderBy("episodeNumber", "asc"),
        );

        unsubscribe = onSnapshot(
          episodesQuery,
          (snapshot) => {
            const episodeData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                ...data,
                id: data.id || doc.id,
                firestoreId: doc.id,
                seriesId,
              };
            });
            setOriginalEpisodes(episodeData);
            setLoading(false);
          },
          (err) => {
            console.error("Error fetching episodes:", err);
            setError(err.message);
            setLoading(false);
          },
        );
      } catch (err) {
        console.error("Error setting up series/episodes listener:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchSeriesAndEpisodes();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [seriesId]);

  // Handle translation when language changes or data updates
  useEffect(() => {
    const handleTranslation = async () => {
      if (loading) return; // Don't translate while still loading

      setTranslating(true);

      try {
        // Translate both series data and episodes in parallel
        const [translatedSeriesData, translatedEpisodes] = await Promise.all([
          translateSeriesData(originalSeriesData),
          translateEpisodes(originalEpisodes),
        ]);

        setSeriesData(translatedSeriesData);
        setEpisodes(translatedEpisodes);
      } catch (error) {
        console.error("Translation error:", error);
        // Fallback to original data
        setSeriesData(originalSeriesData);
        setEpisodes(originalEpisodes);
      } finally {
        setTranslating(false);
      }
    };

    handleTranslation();
  }, [currentLanguage, originalSeriesData, originalEpisodes, loading]);

  return {
    episodes,
    seriesData,
    loading: loading || translating,
    error,
    isTranslating: translating,
    currentLanguage,
    // Provide access to original data if needed
    originalData: {
      episodes: originalEpisodes,
      seriesData: originalSeriesData,
    },
  };
};
